import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import LeadTimeline from '../components/LeadTimeline';
import FollowupGenerator from '../components/FollowupGenerator';
import NurtureSequence from '../components/NurtureSequence';
import { useAuth } from '../contexts/AuthContext';
import {
  Phone,
  EnvelopeSimple,
  MapPin,
  Clock,
  User,
  MagnifyingGlass,
  Plus,
} from '@phosphor-icons/react';
import {
  DndContext,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  DragOverlay,
} from '@dnd-kit/core';
import { toast } from 'sonner';

const getJourneyLabel = (value) => {
  const labels = {
    pronto_atendimento: 'Pronto para Atendimento',
    em_avaliacao: 'Em Avaliação',
    nutricao_futura: 'Nutrição Futura',
    quente: 'Pronto para Atendimento',
    morno: 'Em Avaliação',
    frio: 'Nutrição Futura',
  };
  return labels[value] || value;
};

const getJourneyBadgeClass = (value) => {
  if (value === 'pronto_atendimento' || value === 'quente') return 'bg-emerald-100 text-emerald-700';
  if (value === 'em_avaliacao' || value === 'morno') return 'bg-orange-100 text-orange-700';
  return 'bg-slate-100 text-slate-700';
};


const API = `/api`;

const LeadCard = ({ lead, onClick, isDragOverlay }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: lead.id,
    data: { lead },
    disabled: isDragOverlay,
  });

  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;

  const getStatusColor = (status) => {
    const colors = {
      novo: 'border-l-blue-500 bg-blue-50/30',
      ia_atendendo: 'border-l-purple-500 bg-purple-50/30',
      qualificado: 'border-l-emerald-500 bg-emerald-50/30',
      quente: 'border-l-red-500 bg-red-50/30',
      morno: 'border-l-orange-500 bg-orange-50/30',
      frio: 'border-l-slate-500 bg-slate-50/30',
      em_atendimento: 'border-l-cyan-500 bg-cyan-50/30',
      proposta: 'border-l-indigo-500 bg-indigo-50/30',
      venda_realizada: 'border-l-green-600 bg-green-50/30',
      perdido: 'border-l-slate-700 bg-slate-50/30',
    };
    return colors[status] || 'border-l-slate-400';
  };

  const getScoreBadge = (score) => {
    if (!score) return null;
    if (score >= 70)
      return <span className="text-xs px-2 py-1 rounded bg-emerald-100 text-emerald-700 font-medium">Score: {score}</span>;
    if (score >= 40)
      return <span className="text-xs px-2 py-1 rounded bg-orange-100 text-orange-700 font-medium">Score: {score}</span>;
    return <span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700 font-medium">Score: {score}</span>;
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={(e) => {
        // Avoid triggering click after drag
        if (!isDragging) onClick?.();
      }}
      className={`bg-white border-l-4 ${getStatusColor(
        lead.status
      )} border border-slate-200 rounded-lg p-4 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow duration-200 ${
        isDragging ? 'opacity-30' : ''
      } ${isDragOverlay ? 'shadow-2xl rotate-2' : ''}`}
      data-testid={`lead-card-${lead.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <h4 className="font-semibold text-slate-900 text-sm">{lead.name}</h4>
        {getScoreBadge(lead.score)}
      </div>

      <div className="space-y-2 text-xs text-slate-600">
        <div className="flex items-center gap-2">
          <Phone size={14} />
          <span>{lead.phone}</span>
        </div>
        {lead.email && (
          <div className="flex items-center gap-2">
            <EnvelopeSimple size={14} />
            <span className="truncate">{lead.email}</span>
          </div>
        )}
        {lead.enterprise && (
          <div className="flex items-center gap-2">
            <MapPin size={14} />
            <span className="truncate">{lead.enterprise}</span>
          </div>
        )}
      </div>

      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-1 text-xs text-slate-500">
          <Clock size={12} />
          <span>{lead.source}</span>
        </div>
        {lead.assigned_to_name && (
          <div className="flex items-center gap-1 text-xs text-slate-600 bg-slate-100 px-2 py-1 rounded">
            <User size={12} />
            <span>{lead.assigned_to_name}</span>
          </div>
        )}
      </div>

      {lead.classification && (
        <div className="mt-2">
          <span
            className={`text-xs px-2 py-1 rounded-full font-medium uppercase ${
              getJourneyBadgeClass(lead.classification)
            }`}
          >
            {getJourneyLabel(lead.classification)}
          </span>
        </div>
      )}
    </div>
  );
};

const KanbanColumn = ({ title, status, leads, color, onLeadClick }) => {
  const filteredLeads = leads.filter((lead) => lead.status === status);
  const { setNodeRef, isOver } = useDroppable({ id: status });

  return (
    <div className="flex-shrink-0 w-80" data-testid={`kanban-column-${status}`}>
      <div
        ref={setNodeRef}
        className={`bg-white rounded-lg border h-full flex flex-col transition-colors duration-200 ${
          isOver ? 'border-emerald-500 border-2 bg-emerald-50/50' : 'border-slate-200'
        }`}
      >
        <div className={`p-4 border-b border-slate-200 bg-${color}-50`}>
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-900 text-sm">{title}</h3>
            <span className="px-2 py-1 bg-slate-100 rounded-full text-xs font-medium text-slate-700">
              {filteredLeads.length}
            </span>
          </div>
        </div>
        <div className="flex-1 p-3 space-y-3 overflow-y-auto scrollbar-thin" style={{ maxHeight: 'calc(100vh - 250px)' }}>
          {filteredLeads.map((lead) => (
            <LeadCard key={lead.id} lead={lead} onClick={() => onLeadClick(lead)} />
          ))}
          {filteredLeads.length === 0 && (
            <div className="text-center py-8 text-slate-400 text-sm">
              {isOver ? 'Solte aqui' : 'Nenhum lead'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const LeadDetailModal = ({ lead, onClose, onLeadUpdated }) => {
  const [activeTab, setActiveTab] = useState('details');
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'diretoria';
  if (!lead) return null;

  const returnToAI = async () => {
    if (!window.confirm('Devolver este lead para acompanhamento da IA e iniciar follow-up automático?')) return;
    try {
      const { data } = await axios.post(`${API}/leads/${lead.id}/return-to-ai`, {}, { withCredentials: true });
      toast.success('Lead devolvido para acompanhamento da IA');
      if (onLeadUpdated) onLeadUpdated(data);
      onClose();
    } catch (error) {
      console.error('Erro ao devolver para IA:', error);
      toast.error(error.response?.data?.detail || 'Erro ao devolver lead para IA');
    }
  };


  return (
    <div
      className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="lead-detail-modal"
      >
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">{lead.name}</h2>
              <p className="text-slate-600 mt-1">{lead.enterprise || 'Sem empreendimento'}</p>
              <div className="flex items-center gap-2 mt-3">
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                  lead.conversation_owner === 'corretor'
                    ? 'bg-cyan-100 text-cyan-700'
                    : 'bg-purple-100 text-purple-700'
                }`}>
                  Dono: {lead.conversation_owner === 'corretor' ? 'Corretor' : 'IA'}
                </span>
                {lead.conversation_owner === 'corretor' && (
                  <button
                    onClick={returnToAI}
                    className="text-xs px-3 py-1 rounded-full bg-purple-600 text-white hover:bg-purple-700"
                  >
                    Devolver para IA / Follow-up
                  </button>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600"
              data-testid="close-modal-button"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="mt-5 flex gap-1 border-b border-slate-200 -mb-6">
            <button
              onClick={() => setActiveTab('details')}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'details'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid="tab-details"
            >
              Detalhes
            </button>
            <button
              onClick={() => setActiveTab('timeline')}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'timeline'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid="tab-timeline"
            >
              Histórico Completo
            </button>
            <button
              onClick={() => setActiveTab('followup')}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'followup'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid="tab-followup"
            >
              ✨ Follow-up IA
            </button>
            <button
              onClick={() => setActiveTab('nurture')}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'nurture'
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid="tab-nurture"
            >
              🌱 Nutrição
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {activeTab === 'details' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Telefone</label>
                  <p className="text-slate-900 mt-1">{lead.phone}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Email</label>
                  <p className="text-slate-900 mt-1">{lead.email || 'Não informado'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Origem</label>
                  <p className="text-slate-900 mt-1">{lead.source}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Status</label>
                  <p className="text-slate-900 mt-1 capitalize">{lead.status.replace('_', ' ')}</p>
                </div>
              </div>

              {lead.score && (
                <div>
                  <label className="text-sm font-medium text-slate-700">Score IA</label>
                  <div className="mt-2">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 bg-slate-200 rounded-full h-3">
                        <div
                          className={`h-3 rounded-full ${
                            lead.score >= 70
                              ? 'bg-emerald-500'
                              : lead.score >= 40
                              ? 'bg-orange-500'
                              : 'bg-slate-400'
                          }`}
                          style={{ width: `${lead.score}%` }}
                        ></div>
                      </div>
                      <span className="font-bold text-slate-900">{lead.score}</span>
                    </div>
                  </div>
                </div>
              )}

              {lead.classification && (
                <div>
                  <label className="text-sm font-medium text-slate-700">Classificação</label>
                  <p className="text-slate-900 mt-1 capitalize">{getJourneyLabel(lead.classification)}</p>
                </div>
              )}

              {lead.assigned_to_name && (
                <div>
                  <label className="text-sm font-medium text-slate-700">Responsável</label>
                  <p className="text-slate-900 mt-1">{lead.assigned_to_name}</p>
                </div>
              )}

              {lead.ai_summary && (
                <div>
                  <label className="text-sm font-medium text-slate-700">Resumo IA</label>
                  <p className="text-slate-600 mt-2 text-sm leading-relaxed">{lead.ai_summary}</p>
                </div>
              )}

              {lead.ai_data && Object.keys(lead.ai_data).length > 0 && (
                <div className="grid grid-cols-2 gap-3 p-4 bg-purple-50 rounded-lg border border-purple-200">
                  {lead.ai_data.intencao_compra && (
                    <div>
                      <p className="text-xs font-medium text-purple-900">Intenção de Compra</p>
                      <p className="text-sm text-slate-700 mt-1">{lead.ai_data.intencao_compra}</p>
                    </div>
                  )}
                  {lead.ai_data.faixa_valor && (
                    <div>
                      <p className="text-xs font-medium text-purple-900">Faixa de Valor</p>
                      <p className="text-sm text-slate-700 mt-1">{lead.ai_data.faixa_valor}</p>
                    </div>
                  )}
                  {lead.ai_data.prazo_compra && (
                    <div>
                      <p className="text-xs font-medium text-purple-900">Prazo</p>
                      <p className="text-sm text-slate-700 mt-1 capitalize">{lead.ai_data.prazo_compra}</p>
                    </div>
                  )}
                  {lead.ai_data.regiao_interesse && (
                    <div>
                      <p className="text-xs font-medium text-purple-900">Região</p>
                      <p className="text-sm text-slate-700 mt-1">{lead.ai_data.regiao_interesse}</p>
                    </div>
                  )}
                </div>
              )}

              {lead.created_at && (
                <div>
                  <label className="text-sm font-medium text-slate-700">Criado em</label>
                  <p className="text-slate-900 mt-1">
                    {new Date(lead.created_at).toLocaleString('pt-BR')}
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'timeline' && <LeadTimeline leadId={lead.id} />}
          {activeTab === 'followup' && <FollowupGenerator leadId={lead.id} leadName={lead.name} />}
          {activeTab === 'nurture' && <NurtureSequence leadId={lead.id} leadName={lead.name} isAdmin={isAdmin} />}
        </div>
      </div>
    </div>
  );
};


const NewLeadModal = ({ enterprises, onClose, onSave }) => {
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    enterprise: '',
    source: 'WhatsApp',
    urgency: '',
    notes: '',
  });

  const update = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error('Nome e telefone são obrigatórios');
      return;
    }

    const notesWithUrgency = [
      form.urgency ? `Urgência informada: ${form.urgency}` : '',
      form.notes || ''
    ].filter(Boolean).join('\n\n');

    onSave({
      name: form.name,
      phone: form.phone,
      email: form.email || null,
      enterprise: form.enterprise || null,
      source: form.source,
      notes: notesWithUrgency || null,
    });
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-2xl w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-2xl font-bold text-slate-900">Novo Lead</h2>
          <p className="text-slate-600 mt-1">Cadastro manual para leads de indicação, stand, telefone ou WhatsApp</p>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-1">
              <label className="text-sm font-medium text-slate-700">Nome *</label>
              <input className="mt-1 w-full border rounded-lg px-3 py-2" value={form.name} onChange={(e) => update('name', e.target.value)} />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">Telefone *</label>
              <input className="mt-1 w-full border rounded-lg px-3 py-2" value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="(87) 99999-9999" />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">Email</label>
              <input className="mt-1 w-full border rounded-lg px-3 py-2" value={form.email} onChange={(e) => update('email', e.target.value)} />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">Origem</label>
              <select className="mt-1 w-full border rounded-lg px-3 py-2" value={form.source} onChange={(e) => update('source', e.target.value)}>
                <option>WhatsApp</option>
                <option>Instagram</option>
                <option>Facebook Ads</option>
                <option>Google Ads</option>
                <option>Indicação</option>
                <option>Stand de Vendas</option>
                <option>Telefone</option>
                <option>Site</option>
                <option>Corretor</option>
                <option>Outro</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">Empreendimento</label>
              <select className="mt-1 w-full border rounded-lg px-3 py-2" value={form.enterprise} onChange={(e) => update('enterprise', e.target.value)}>
                <option value="">Selecione</option>
                {enterprises.map((enterprise) => (
                  <option key={enterprise.id} value={enterprise.name}>{enterprise.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">Urgência</label>
              <select className="mt-1 w-full border rounded-lg px-3 py-2" value={form.urgency} onChange={(e) => update('urgency', e.target.value)}>
                <option value="">Não informado</option>
                <option>Imediata</option>
                <option>30 dias</option>
                <option>90 dias</option>
                <option>6 meses</option>
                <option>Apenas pesquisando</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="text-sm font-medium text-slate-700">Observações</label>
              <textarea className="mt-1 w-full border rounded-lg px-3 py-2 min-h-[100px]" value={form.notes} onChange={(e) => update('notes', e.target.value)} placeholder="Ex.: Veio pelo feirão, quer apartamento de 2 quartos, tem FGTS..." />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-5 py-2 rounded-lg border border-slate-300 text-slate-700">
              Cancelar
            </button>
            <button className="px-5 py-2 rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700">
              Salvar Lead
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


const Kanban = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showNewLead, setShowNewLead] = useState(false);
  const [enterprises, setEnterprises] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeDragLead, setActiveDragLead] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  useEffect(() => {
    fetchLeads();
    fetchEnterprises();
  }, []);

  const fetchLeads = async () => {
    try {
      const { data } = await axios.get(`${API}/leads`, { withCredentials: true });
      setLeads(data);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEnterprises = async () => {
    try {
      const { data } = await axios.get(`${API}/enterprises`, { withCredentials: true });
      setEnterprises(data);
    } catch (error) {
      console.error('Error fetching enterprises:', error);
    }
  };

  const createLead = async (payload) => {
    try {
      const { data } = await axios.post(`${API}/leads`, payload, { withCredentials: true });
      setLeads((prev) => [data, ...prev]);
      setShowNewLead(false);
      toast.success('Lead cadastrado com sucesso');
    } catch (error) {
      console.error('Error creating lead:', error);
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar lead');
    }
  };

  const handleDragStart = (event) => {
    const lead = leads.find((l) => l.id === event.active.id);
    setActiveDragLead(lead);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveDragLead(null);
    if (!over) return;

    const leadId = active.id;
    const newStatus = over.id;
    const lead = leads.find((l) => l.id === leadId);
    if (!lead || lead.status === newStatus) return;

    // Optimistic update
    const oldStatus = lead.status;
    setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status: newStatus } : l)));

    try {
      await axios.patch(`${API}/leads/${leadId}`, { status: newStatus }, { withCredentials: true });
      toast.success(`Lead movido para "${newStatus.replace('_', ' ')}"`);
    } catch (error) {
      // Revert on error
      setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status: oldStatus } : l)));
      toast.error('Erro ao mover lead');
    }
  };

  const columns = [
    { title: 'Novo Lead', status: 'novo', color: 'blue' },
    { title: 'IA Atendendo', status: 'ia_atendendo', color: 'purple' },
    { title: 'Qualificado', status: 'qualificado', color: 'emerald' },
    { title: 'Pronto para Atendimento', status: 'pronto_atendimento', color: 'red' },
    { title: 'Em Avaliação', status: 'em_avaliacao', color: 'orange' },
    { title: 'Nutrição Futura', status: 'nutricao_futura', color: 'slate' },
    { title: 'Em Atendimento', status: 'em_atendimento', color: 'cyan' },
    { title: 'Proposta', status: 'proposta', color: 'indigo' },
    { title: 'Venda Realizada', status: 'venda_realizada', color: 'green' },
    { title: 'Perdido', status: 'perdido', color: 'slate' },
  ];

  const filteredLeads = leads.filter(
    (lead) =>
      lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.phone.includes(searchTerm) ||
      (lead.email && lead.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="w-16 h-16 border-4 border-blue-900 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" data-testid="kanban-title">
              CRM Kanban
            </h1>
            <p className="text-slate-600 mt-1">Gestão visual do funil de vendas</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="kanban-search-input"
              />
            </div>
          </div>
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-4" style={{ minWidth: 'max-content' }}>
              {columns.map((column) => (
                <KanbanColumn
                  key={column.status}
                  title={column.title}
                  status={column.status}
                  leads={filteredLeads}
                  color={column.color}
                  onLeadClick={setSelectedLead}
                />
              ))}
            </div>
          </div>
          <DragOverlay>
            {activeDragLead ? <LeadCard lead={activeDragLead} isDragOverlay /> : null}
          </DragOverlay>
        </DndContext>
      </div>

      {selectedLead && <LeadDetailModal lead={selectedLead} onClose={() => setSelectedLead(null)} onLeadUpdated={(updatedLead) => setLeads((prev) => prev.map((l) => l.id === updatedLead.id ? updatedLead : l))} />}
      {showNewLead && <NewLeadModal enterprises={enterprises} onClose={() => setShowNewLead(false)} onSave={createLead} />}
    </Layout>
  );
};

export default Kanban;